import { createFileRoute, Outlet } from "@tanstack/react-router";
import { MemoryMonitor } from "@/components/debug/memory-monitor";
import { DetailsPanel, HoverPreviewPanel, IconBar, SidebarV2Provider } from "@/components/sidebar";
import { MobileBottomNav } from "@/components/sidebar/mobile-nav";
import { PageChromeProvider, usePageChromeState } from "@/components/ui/page-chrome";
import { TopBar } from "@/components/ui/top-bar";
import { useAuth } from "@/features/auth/use-auth";
import { OnlineAdminsProbe } from "@/features/helpdesk/online-admins";
import { UsageTracker } from "@/features/portal-usage/usage-tracker";
import { isEmbedded } from "@/lib/auth-api";
import { ensureAuthenticated } from "@/lib/route-guards";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_auth")({
	beforeLoad: async () => {
		await ensureAuthenticated();
	},
	component: AuthLayout,
});

function DebugMonitorSlot({ isSuperAdmin }: { isSuperAdmin: boolean }) {
	// Na telefonie monitor wlacza menu „⋯” (Debug); na desktopie jest stale.
	const debugOnMobile = usePageChromeState()?.debugMonitor ?? false;
	if (!isSuperAdmin) return null;
	return (
		<div
			className={cn(
				"fixed bottom-[4.5rem] left-2 z-50 md:block md:bottom-4 md:left-[calc(1rem+80px)]",
				debugOnMobile ? "block" : "hidden",
			)}
		>
			<MemoryMonitor />
		</div>
	);
}

function AuthLayout() {
	const { user } = useAuth();
	const isSuperAdmin = user?.roles?.includes("super_admin") ?? false;

	// Embedded (panel agenta w WebView): goła zawartość bez chrome (sidebar/topbar/
	// mobile-nav) — panel ma wyglądać jak wąski widok, nie cała aplikacja.
	if (isEmbedded()) {
		return (
			<main className="flex h-dvh flex-col overflow-hidden bg-[var(--background)]">
				<Outlet />
			</main>
		);
	}

	return (
		<SidebarV2Provider>
			<UsageTracker />
			<OnlineAdminsProbe />
			<PageChromeProvider>
				<div className="flex h-dvh overflow-hidden bg-[var(--background)]">
					<div className="hidden md:flex md:h-full">
						<IconBar />
						<DetailsPanel />
						<HoverPreviewPanel />
					</div>
					<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
						<TopBar />
						{/* Nawigacja dolna jest czescia ukladu (nie fixed), wiec nie
						    zaslania tresci. Przewijanie strony w bok na telefonie lapie
						    e2e/mobile-layout.spec.ts (scrollWidth main). */}
						<main className="flex flex-1 flex-col overflow-auto">
							<Outlet />
						</main>
						<MobileBottomNav />
					</div>
				</div>
				<DebugMonitorSlot isSuperAdmin={isSuperAdmin} />
			</PageChromeProvider>
		</SidebarV2Provider>
	);
}
